/**
 * Звук клиента (C7). `startAudio()` — одна строка в main.tsx: грузит манифест, слушает `battleStore` (события снимка →
 * эффекты и музыка), `appStore` (пауза, свёрнуто) и `audioSettings` (громкость, «без звука»).
 *
 * Из UI: `playSound("purchase")` (покупки), клики по `<button>` озвучиваются сами (`ui_click`; `data-nosound` — без звука).
 */
import { appStore, battleStore, isBattlePaused } from "../app/store";
import { createAudioEngine, type AudioEngine } from "./engine";
import { musicAction, pickFormat, planTick, type AudioManifest } from "./manifest";
import { audioSettings } from "./settings";

export { audioSettings, setMuted, setVolume, toggleMuted } from "./settings";
export { SoundToggle } from "./SoundToggle";

let engine: AudioEngine | null = null;

/** Эффект из UI (`purchase`, `ui_click` и др.). До старта звука и без манифеста — тишина. */
export function playSound(id: string, opts?: { gain?: number }): void {
  engine?.play(id, opts);
}

export interface AudioStart {
  manifestUrl?: string;
}

export async function startAudio(opts: AudioStart = {}): Promise<void> {
  if (engine) return;
  let manifest: AudioManifest;
  try {
    const res = await fetch(opts.manifestUrl ?? "/audio/manifest.json");
    if (!res.ok) throw new Error(`manifest ${res.status}`);
    manifest = (await res.json()) as AudioManifest;
  } catch (e) {
    console.warn("[audio] manifest not loaded, sound off", e);
    return;
  }
  const probe = typeof Audio === "undefined" ? null : new Audio();
  const eng = createAudioEngine(manifest, pickFormat(probe));
  engine = eng;
  let lastFrame = -1;
  let musicDecided = false;

  // Разблокировка по касанию. Слушатели не снимаем: iOS ставит контекст в `interrupted` (звонок, сворачивание Telegram),
  // и вернуть его можно только новым жестом.
  const onGesture = (): void => {
    if (eng.state !== "running") eng.unlock();
  };
  for (const e of ["pointerdown", "touchend", "keydown"] as const) document.addEventListener(e, onGesture, true);
  document.addEventListener("click", (e) => {
    const el = e.target instanceof Element ? e.target.closest("button, [role=button]") : null;
    if (el && !el.hasAttribute("data-nosound")) eng.play("ui_click");
  });

  const applyOutput = (): void => eng.setOutput(audioSettings.get().muted, audioSettings.get().volume);
  applyOutput();
  audioSettings.subscribe(applyOutput);

  const applyPause = (): void => {
    const s = appStore.get();
    eng.setHidden(s.pause.includes("hidden"));
    eng.setBattlePaused(isBattlePaused(s));
  };
  applyPause();
  appStore.subscribe((s, prev) => {
    if (s.runEpoch !== prev.runEpoch) musicDecided = false;
    applyPause();
  });

  battleStore.subscribe((snap) => {
    if (!snap || snap.frame === lastFrame) return;
    lastFrame = snap.frame;
    for (const id of planTick(manifest, snap.events)) eng.play(id);
    for (const e of snap.events) {
      const a = musicAction(manifest, e);
      if (!a) continue;
      musicDecided = true;
      eng.playMusic("stop" in a ? null : a.play);
    }
    // подключились посреди волны (событие уже было) — музыка по фазе волны
    if (!musicDecided && (snap.wave.phase === "fight" || snap.wave.phase === "intro")) {
      musicDecided = true;
      eng.playMusic(snap.wave.isBoss ? "music_boss" : "music_battle");
    }
  });
}
